/**
 * POS session store.
 *
 * Tracks the current cashier session (open register → close register).
 * The session is loaded from the API on startup — not persisted locally.
 */

import type { POSSession, CashMovement } from '@burmazon/shared';
import { create } from 'zustand';

interface SessionState {
  /** Active session, or null if the register is not open. */
  session: POSSession | null;

  /** Whether the initial session check is still in flight. */
  isLoading: boolean;

  /** Whether the open-register modal should be shown. */
  showOpenModal: boolean;

  /** Whether the cash movement drawer is open. */
  showCashDrawer: boolean;

  /** Whether the close-register wizard is open. */
  showCloseWizard: boolean;

  // ── Actions ─────────────────────────────────────────────────────────────────

  setSession: (s: POSSession | null) => void;
  setLoading: (v: boolean) => void;
  openRegister: (s: POSSession) => void;
  closeRegister: () => void;

  /** Append a new cash movement to the current session's list. */
  addCashMovement: (m: CashMovement) => void;

  setShowCashDrawer: (v: boolean) => void;
  setShowCloseWizard: (v: boolean) => void;
  setShowOpenModal: (v: boolean) => void;
}

export const useSessionStore = create<SessionState>()((set) => ({
  session: null,
  isLoading: true,
  showOpenModal: false,
  showCashDrawer: false,
  showCloseWizard: false,

  setSession: (s) => set({ session: s }),
  setLoading: (v) => set({ isLoading: v }),

  openRegister: (s) => set({ session: s, showOpenModal: false, isLoading: false }),

  closeRegister: () => set({ session: null, showOpenModal: true, showCloseWizard: false }),

  addCashMovement: (m) =>
    set((state) => {
      if (!state.session) return {};
      return {
        session: {
          ...state.session,
          cashMovements: [...(state.session.cashMovements ?? []), m],
        },
      };
    }),

  setShowCashDrawer: (v) => set({ showCashDrawer: v }),
  setShowCloseWizard: (v) => set({ showCloseWizard: v }),
  setShowOpenModal: (v) => set({ showOpenModal: v }),
}));
